import {
  CarouselControl,
  CarouselPagination,
  tv,
  useBreakpoint,
  UnstyledText,
} from "@tournated-com/ui";
import { motion } from "framer-motion";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { MessageKey, useT } from "@/i18n";
import { OnboardingStepView } from "@/types";
import { cn, TextButton } from "@/ui";

const GAP = 12;
const PADDING_X = 4;

type OnboardingCardProps = {
  title: MessageKey;
  steps: OnboardingStepView[];
  onMarkDone: (id: string) => void;
  onSkipAll: () => void;
  isSaving?: boolean;
  className?: string;
};

/**
 * Compact onboarding overview carousel. Shows ONLY incomplete steps — completed
 * ones disappear as the user builds things out — and hides itself entirely once
 * every step is done. Each card carries a "Mark as done" text button; a
 * "Skip all" text button sits in the header. Presentation only: completion and
 * skip are owned by the container via `onMarkDone` / `onSkipAll`.
 */
export const OnboardingCard = ({
  title,
  steps,
  onMarkDone,
  onSkipAll,
  isSaving,
  className,
}: OnboardingCardProps) => {
  const t = useT();

  const visibleSteps = useMemo(() => steps.filter((step) => !step.isCompleted), [steps]);

  // How many cards fit in the carousel viewport scales with screen width so the
  // cards never collapse on phones/tablets (breakpoints: sm 600, md 900, lg 1200, xl 1536).
  const isSmDown = useBreakpoint("sm", "down");
  const isMdDown = useBreakpoint("md", "down");
  const isLgDown = useBreakpoint("lg", "down");
  const isXlDown = useBreakpoint("xl", "down");
  const itemsPerView = isSmDown ? 1 : isMdDown ? 2 : isLgDown ? 3 : isXlDown ? 4 : 5;

  const maxIndex = Math.max(0, visibleSteps.length - itemsPerView);

  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [itemWidth, setItemWidth] = useState(0);

  // useLayoutEffect measures before paint so cards render at full width instead
  // of flashing from 0 on first mount; a ResizeObserver keeps it correct on
  // resize. Re-runs when the visible count or per-view count changes.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const calculateWidth = () => {
      const containerWidth = el.offsetWidth - PADDING_X * 2;
      const totalGap = GAP * (itemsPerView - 1);
      setItemWidth((containerWidth - totalGap) / itemsPerView);
    };

    calculateWidth();
    const observer = new ResizeObserver(calculateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, [itemsPerView, visibleSteps.length]);

  // Keep the active index in range when the viewport (and thus maxIndex) shrinks.
  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, maxIndex));
  }, [maxIndex]);

  const canScrollPrev = currentIndex > 0;
  const canScrollNext = currentIndex < maxIndex;

  const scrollPrev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const scrollNext = useCallback(() => {
    setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
  }, [maxIndex]);

  const handleKeyboardNavigation = useCallback(
    (direction: "left" | "right") => {
      if (direction === "left" && canScrollPrev) {
        scrollPrev();
      } else if (direction === "right" && canScrollNext) {
        scrollNext();
      }
    },
    [canScrollPrev, canScrollNext, scrollPrev, scrollNext]
  );

  const translateX = -(currentIndex * (itemWidth + GAP));

  if (visibleSteps.length === 0) return null;

  return (
    <div className={cn("flex flex-col gap-5 w-full", className)}>
      <div className="flex gap-2 items-center justify-between">
        <h2 className="preset-label-lg text-new-primary">{t(title)}</h2>
        <div className="flex gap-2 items-center">
          <TextButton color="muted" size="md" onPress={onSkipAll} isDisabled={isSaving}>
            {t("general.actions.skipAll")}
          </TextButton>
          <CarouselControl
            direction="prev"
            color="dark"
            onPress={scrollPrev}
            isDisabled={!canScrollPrev}
          />
          <CarouselControl
            direction="next"
            color="dark"
            onPress={scrollNext}
            isDisabled={!canScrollNext}
          />
        </div>
      </div>

      <div className="flex flex-col gap-3 w-full">
        <div
          className="overflow-hidden py-1 focus:outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-focus focus-visible:outline-offset-2 focus-visible:rounded-md"
          ref={containerRef}
          style={{ paddingLeft: PADDING_X, paddingRight: PADDING_X }}
          tabIndex={0}
          role="region"
          aria-label={t(title)}
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
              e.preventDefault();
              e.stopPropagation();
              handleKeyboardNavigation(e.key === "ArrowLeft" ? "left" : "right");
            }
          }}
        >
          <motion.div
            className="flex items-stretch"
            style={{ gap: `${GAP}px` }}
            animate={{ x: translateX }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            {visibleSteps.map((step) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.id}
                  className={itemVariants()}
                  style={{ width: itemWidth, minWidth: itemWidth, maxWidth: itemWidth }}
                >
                  <div className="flex items-start justify-between w-full min-w-0 gap-2">
                    <div
                      aria-hidden="true"
                      className="rounded-md size-10 flex items-center justify-center p-[3px] bg-brand-subtle-1 text-brand shrink-0"
                    >
                      <Icon width={30} height={30} />
                    </div>
                    <TextButton
                      color="brand"
                      size="md"
                      onPress={() => onMarkDone(step.id)}
                      isDisabled={isSaving}
                      className="shrink-0"
                    >
                      {t("general.actions.markAsDone")}
                    </TextButton>
                  </div>

                  <div className="flex flex-col gap-0.5 w-full min-w-0 overflow-hidden">
                    <UnstyledText className="preset-label-xl text-new-primary break-words [overflow-wrap:anywhere]">
                      {t(step.title)}
                    </UnstyledText>
                    <UnstyledText className="preset-body-lg text-secondary break-words [overflow-wrap:anywhere]">
                      {t(step.description)}
                    </UnstyledText>
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>

        {maxIndex > 0 && (
          <div className="flex items-center justify-center gap-2.5 relative w-full">
            <CarouselPagination
              totalCount={maxIndex + 1}
              page={currentIndex + 1}
              onPageChange={(page) => setCurrentIndex(page - 1)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const itemVariants = tv({
  base: [
    "group flex flex-col gap-2 min-w-0 self-stretch overflow-hidden",
    "p-4 min-h-[150px]",
    "bg-surface-1 border border-surface-2 rounded-md border-solid",
    "transition-colors duration-150",
  ],
});
